// src/services/sanitaryProductService.js
// =============================================================================
// Frontend API wrapper for Sanitary Documentation / Disinfection & Hygiene
// =============================================================================

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[SanitaryProductService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertProductId(context, productId) {
  if (productId === undefined || productId === null || `${productId}`.trim() === "") {
    const e = new Error(`${context}: productId is required.`);
    e.code = "PRODUCT_ID_MISSING";
    throw e;
  }
}

function buildParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

export async function listSanitaryProducts(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/products", {
      withCredentials: true,
      params: buildParams(params),
    });
    // Preserve the legacy array contract used by Rule/Solution pickers. The
    // backend keeps returning an array for callers using `limit` without page.
    if (Array.isArray(res?.data)) return res.data;
    return Array.isArray(res?.data?.items) ? res.data.items : [];
  } catch (err) {
    throwApiError("GET /sanitary/products", err);
  }
}

export async function listSanitaryProductsPaged(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/products", {
      withCredentials: true,
      params: buildParams(params),
    });
    const data = res?.data;
    if (Array.isArray(data)) {
      return {
        items: data,
        pagination: {
          page: 1,
          page_size: data.length || Number(params.page_size || 25),
          total: data.length,
          page_count: 1,
        },
        summary: {
          total: data.length,
          profiled: data.filter((row) => row?.is_sanitary).length,
          active_profiles: data.filter((row) => row?.is_sanitary && row?.profile?.is_active !== false).length,
        },
      };
    }
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      pagination: {
        page: Number(data?.pagination?.page || params.page || 1),
        page_size: Number(data?.pagination?.page_size || params.page_size || 25),
        total: Number(data?.pagination?.total || 0),
        page_count: Math.max(1, Number(data?.pagination?.page_count || 1)),
      },
      summary: {
        total: Number(data?.summary?.total || 0),
        profiled: Number(data?.summary?.profiled || 0),
        active_profiles: Number(data?.summary?.active_profiles || 0),
      },
    };
  } catch (err) {
    throwApiError("GET /sanitary/products (paged)", err);
  }
}

export async function getSanitaryProduct(productId) {
  assertProductId("getSanitaryProduct", productId);
  try {
    const res = await apiClient.get(`/sanitary/products/${productId}`, {
      withCredentials: true,
    });
    return res?.data;
  } catch (err) {
    throwApiError(`GET /sanitary/products/${productId}`, err);
  }
}

export async function saveSanitaryProductProfile(productId, payload) {
  assertProductId("saveSanitaryProductProfile", productId);
  try {
    const res = await apiClient.put(
      `/sanitary/products/${productId}/profile`,
      payload || {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    return res?.data;
  } catch (err) {
    throwApiError(`PUT /sanitary/products/${productId}/profile`, err);
  }
}

export async function setSanitaryProductProfileActive(productId, isActive) {
  assertProductId("setSanitaryProductProfileActive", productId);
  try {
    const res = await apiClient.patch(
      `/sanitary/products/${productId}/profile/active`,
      { is_active: Boolean(isActive) },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    return res?.data;
  } catch (err) {
    throwApiError(`PATCH /sanitary/products/${productId}/profile/active`, err);
  }
}

export async function deleteSanitaryProductProfile(productId) {
  assertProductId("deleteSanitaryProductProfile", productId);
  try {
    const res = await apiClient.delete(`/sanitary/products/${productId}/profile`, {
      withCredentials: true,
    });
    return res?.data;
  } catch (err) {
    throwApiError(`DELETE /sanitary/products/${productId}/profile`, err);
  }
}

export default {
  listSanitaryProducts,
  listSanitaryProductsPaged,
  getSanitaryProduct,
  saveSanitaryProductProfile,
  setSanitaryProductProfileActive,
  deleteSanitaryProductProfile,
};
